import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MapPin, Check, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { BRAND, SERVICES, TESTIDS } from "@/lib/site";
import SEO from "@/components/SEO";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "9ad558c7-cc35-4e4d-aafd-f5a321efbd39";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: SERVICES[0].title,
  goal: "",
  availability: "",
};

export default function BookContact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
  const response = await fetch(WEB3FORMS_ENDPOINT, {
  method: "POST",
  credentials: "omit",
  body: new URLSearchParams({
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: `BW MATRIX inquiry — ${form.service}`,
    from_name: "BW MATRIX Website",
    ...form,
  }),
});

const result = await response.json();
if (!response.ok || !result.success) {
  throw new Error(result.message || "Inquiry failed");
}


setDone(true);
      setForm(initialForm);
      toast.success("Inquiry sent — I'll be in touch shortly.");
    } catch (err) {
     const detail = err?.response?.data?.message || err?.message || "Something went wrong. Please try again or call.";
      toast.error(typeof detail === "string" ? detail : "Please check your inputs and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO
        title="Book / Contact"
        path="/book"
        description="Book a consultation with BW MATRIX — Fascial Stretch Therapy, mobility training, and personal training in Mississauga / GTA. Call (647) 449-2828."
      />
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 md:pb-24">
          <p className="label-eyebrow mb-6">Book / Contact</p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase font-bold text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight text-white"
          >
            Let's<br />work.
          </motion.h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed">
            Tell me a bit about your goals and preferred availability. I'll follow up with next steps.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Details */}
          <div className="md:col-span-5 space-y-10">
            <div>
              <p className="label-eyebrow mb-3">Direct</p>
              <a
                href={`tel:${BRAND.phone}`}
                className="group inline-flex items-center gap-4 hover:text-white/70 transition-colors"
              >
                <Phone size={18} className="text-white" />
                <span className="font-display uppercase text-3xl md:text-4xl font-bold text-white">
                  {BRAND.phoneDisplay}
                </span>
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="mt-4 group inline-flex items-center gap-4 hover:text-white/70 transition-colors"
              >
                <Mail size={18} className="text-white" />
                <span className="text-white text-lg break-all">
                  {BRAND.email}
                </span>
              </a>
            </div>
            <div>
              <p className="label-eyebrow mb-3">Service Area</p>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-white mt-1" />
                <p className="text-white text-lg">{BRAND.serviceArea}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-7">
            <div className="border border-white/10 bg-[#141417] p-6 md:p-10">
              {done ? (
                <div data-testid={TESTIDS.contact.success} className="py-8">
                  <p className="label-eyebrow mb-4">Received</p>
                  <h3 className="font-display uppercase text-3xl md:text-4xl font-bold text-white leading-tight tracking-tight">
                    Thanks — your inquiry is in.
                  </h3>
                  <p className="mt-4 text-zinc-300">
                    I'll follow up shortly at the email you provided. If it's urgent, feel free to call {BRAND.phoneDisplay}.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="contact-send-another"
                    className="mt-8 inline-flex items-center gap-3 border border-white/60 text-white px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/10 transition-colors"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid={TESTIDS.contact.form} className="space-y-6">
                  <Field label="Name" required>
                    <input
                      data-testid={TESTIDS.contact.name}
                      required
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className="w-full bg-[#0A0A0C] border border-white/15 focus:border-white focus:ring-1 focus:ring-white outline-none px-4 py-3 text-white placeholder:text-zinc-600"
                      placeholder="Your full name"
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Email" required>
                      <input
                        data-testid={TESTIDS.contact.email}
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="w-full bg-[#0A0A0C] border border-white/15 focus:border-white focus:ring-1 focus:ring-white outline-none px-4 py-3 text-white placeholder:text-zinc-600"
                        placeholder="you@email.com"
                      />
                    </Field>
                    <Field label="Phone" required>
                      <input
                        data-testid={TESTIDS.contact.phone}
                        required
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        className="w-full bg-[#0A0A0C] border border-white/15 focus:border-white focus:ring-1 focus:ring-white outline-none px-4 py-3 text-white placeholder:text-zinc-600"
                        placeholder="(647) 000-0000"
                      />
                    </Field>
                  </div>

                  <Field label="Service Interested In" required>
                    <select
                      data-testid={TESTIDS.contact.service}
                      required
                      value={form.service}
                      onChange={update("service")}
                      className="w-full bg-[#0A0A0C] border border-white/15 focus:border-white focus:ring-1 focus:ring-white outline-none px-4 py-3 text-white"
                    >
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                      <option value="Not sure yet">Not sure yet — help me choose</option>
                    </select>
                  </Field>

                  <Field label="Main goal or issue" required>
                    <textarea
                      data-testid={TESTIDS.contact.goal}
                      required
                      value={form.goal}
                      onChange={update("goal")}
                      rows={4}
                      className="w-full bg-[#0A0A0C] border border-white/15 focus:border-white focus:ring-1 focus:ring-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 resize-none"
                      placeholder="What's going on with your body, or what you're training for?"
                    />
                  </Field>

                  <Field label="Preferred availability" required>
                    <input
                      data-testid={TESTIDS.contact.availability}
                      required
                      type="text"
                      value={form.availability}
                      onChange={update("availability")}
                      className="w-full bg-[#0A0A0C] border border-white/15 focus:border-white focus:ring-1 focus:ring-white outline-none px-4 py-3 text-white placeholder:text-zinc-600"
                      placeholder="e.g. Weekday evenings, Saturday mornings"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid={TESTIDS.contact.submit}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold hover:bg-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed group"
                  >
                    {loading ? "Sending…" : "Send Inquiry"}
                    {!loading && (
                      <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    )}
                  </button>

                  <p className="text-xs text-zinc-500 leading-relaxed">
                    By submitting, you agree to be contacted about your inquiry. We do not share your information.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">
        {label}
        {required && <span className="text-white/40 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
